import React from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';

/*
 * ConsentWithdrawnBanner (TDI-481)
 *
 * The note the support side sees on a withdrawn referral they have opened
 * from the Consent withdrawn list: who withdrew, when, how it reached the
 * service, and where the referral had got to. The record below it is read
 * only; every triage, letter and handover action is gone.
 *
 * Same family as the Assessment platform's AssessmentEndedBanner (amber,
 * left-aligned, ExclamationIcon): "something changed this patient's
 * pathway". No design frame exists for the BTH side; wording is a draft for
 * Jasmine's gate.
 */

// Keyed on the stages rms_consent_withdrawal.py records. The middle one is
// 'sent_waiting_for_review'; keying it as 'sent' silently dropped the stage
// clause from the banner for referrals withdrawn while waiting for review.
const STAGE_WORDS = {
  in_progress: 'the forms were still being filled in',
  sent_waiting_for_review: 'it had been sent and was waiting for review',
  in_review: 'it was being reviewed',
};

const ROUTE_WORDS = {
  family: 'through the portal',
  staff: 'by telling the service',
};

function withdrawnOnLabel(withdrawal) {
  const raw = withdrawal?.reportedAt || withdrawal?.withdrawnAt;
  const date = raw?.toDate ? raw.toDate() : (raw ? new Date(raw) : null);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ConsentWithdrawnBanner({ patientRecord }) {
  const withdrawal = patientRecord?.consentWithdrawal || {};
  const when = withdrawnOnLabel(withdrawal);
  const stage = STAGE_WORDS[withdrawal.stage] || null;
  const route = ROUTE_WORDS[withdrawal.route] || null;
  const method = withdrawal.method ? ` (${withdrawal.method})` : '';

  return (
    <div
      role="status"
      className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left"
    >
      <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
      <div className="min-w-0 text-sm text-amber-900">
        <p className="font-semibold">Consent withdrawn by the parent or carer</p>
        <p className="mt-1">
          {when ? `Withdrawn on ${when}` : 'Withdrawn'}
          {route ? ` ${route}${method}` : ''}
          {stage ? `, while ${stage}` : ''}.
          {' '}This record is read only. Nothing further happens on the referral unless the parent or carer gives their consent again.
        </p>
        {withdrawal.note && (
          <p className="mt-1">Note from the service: {withdrawal.note}</p>
        )}
      </div>
    </div>
  );
}
